/**
 * InvoicesBoard — Iter-156 · Phase 1
 *
 * Filterable NECS invoice list with per-row Save (PDF) and Send
 * (Resend email) actions. Reads from /api/iter156/invoices which
 * honours query params for service_type / property_id / start / end.
 */
import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Download, Send, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = '#C9A962';

const STATUS_COLORS = {
  GREEN: '#10b981', BLUE: '#3b82f6', YELLOW: '#facc15',
  ORANGE: '#f97316', RED: '#ef4444', BLACK: '#111827',
};

const inputCls = 'rounded-md px-3 py-2 text-sm text-white bg-[#141414] border-2 border-[#3f3f46] focus:border-[#C9A962] focus:outline-none';

export default function InvoicesBoard() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [filters, setFilters] = useState({ service_type: '', property_id: '', start: '', end: '', status: '' });

  const load = async () => {
    setBusy(true); setErr('');
    try {
      const params = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
      const r = await fetch(`${API}/api/iter156/invoices?${params.toString()}`);
      const d = await r.json();
      setRows(d.invoices || []);
    } catch (e) { setErr(String(e.message)); }
    setBusy(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async (num) => {
    setNotice(`Downloading ${num}.pdf …`);
    const url = `${API}/api/iter156/invoices/${num}/pdf`;
    // trigger native download
    const a = document.createElement('a');
    a.href = url; a.download = `${num}.pdf`; a.click();
    setTimeout(() => setNotice(''), 1500);
  };

  const send = async (num) => {
    setNotice(''); setErr('');
    try {
      const r = await fetch(`${API}/api/iter156/invoices/${num}/send`, { method: 'POST' });
      const d = await r.json();
      if (d.status === 'sent') {
        setNotice(`Sent ${num} to client (via Resend).`);
      } else if (d.status === 'integration_pending') {
        setNotice(`${num}: ${d.note}`);
      } else if (!r.ok) {
        setErr(d.detail || 'send_failed');
      }
    } catch (e) { setErr(String(e.message)); }
  };

  return (
    <div className="space-y-4" data-testid="invoices-board">
      <div className="flex items-end flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1">Service Type</p>
          <input className={inputCls} placeholder="e.g. office" value={filters.service_type} onChange={(e) => setFilters({ ...filters, service_type: e.target.value })} data-testid="invoices-filter-service" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1">Property ID</p>
          <input className={inputCls} placeholder="PROP-…" value={filters.property_id} onChange={(e) => setFilters({ ...filters, property_id: e.target.value })} data-testid="invoices-filter-property" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1">From</p>
          <input type="date" className={inputCls} value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} data-testid="invoices-filter-start" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1">To</p>
          <input type="date" className={inputCls} value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} data-testid="invoices-filter-end" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1">Status</p>
          <select className={inputCls} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="invoices-filter-status">
            <option value="">All</option>
            {Object.keys(STATUS_COLORS).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <Button onClick={load} disabled={busy} className="font-bold uppercase tracking-widest" style={{ backgroundColor: GOLD, color: '#111' }} data-testid="invoices-apply-filters">
          <RefreshCw className="h-4 w-4 mr-1" /> {busy ? 'LOADING…' : 'APPLY'}
        </Button>
      </div>

      {notice && <p className="text-xs" style={{ color: GOLD }} data-testid="invoices-notice">{notice}</p>}
      {err && <p className="text-xs text-red-400" data-testid="invoices-error">{err}</p>}

      <div className="overflow-x-auto rounded-lg border border-zinc-700">
        <table className="w-full text-sm" data-testid="invoices-table">
          <thead>
            <tr className="bg-[#141414] text-zinc-400 text-[10px] uppercase tracking-widest">
              <th className="text-left p-3">Invoice #</th>
              <th className="text-left p-3">Customer</th>
              <th className="text-left p-3">Property</th>
              <th className="text-left p-3">Service</th>
              <th className="text-right p-3">Total</th>
              <th className="text-center p-3">Status</th>
              <th className="text-left p-3">Due</th>
              <th className="text-center p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !busy && (
              <tr><td colSpan="8" className="p-6 text-center text-zinc-500 text-sm" data-testid="invoices-empty">No invoices match — create one via POST /api/iter156/invoices or adjust filters.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.invoice_number} className="border-t border-zinc-800 hover:bg-[#111] transition" data-testid={`invoice-row-${r.invoice_number}`}>
                <td className="p-3 font-bold" style={{ color: GOLD }}>{r.invoice_number}</td>
                <td className="p-3 text-white">{r.customer_legal_name}</td>
                <td className="p-3 text-zinc-300">{r.property_name || '—'}</td>
                <td className="p-3 text-zinc-300">{r.service_package || '—'}</td>
                <td className="p-3 text-right text-white">${Number(r.total).toLocaleString()}</td>
                <td className="p-3 text-center">
                  <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: STATUS_COLORS[r.status_color] || '#333', color: r.status_color === 'YELLOW' ? '#111' : '#fff' }} data-testid={`invoice-status-${r.invoice_number}`}>
                    {r.status_color}
                  </span>
                </td>
                <td className="p-3 text-zinc-400">{(r.due_date || '').slice(0, 10)}</td>
                <td className="p-3 text-center">
                  <button onClick={() => save(r.invoice_number)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#C9A962] text-[#C9A962] hover:bg-[#C9A962] hover:text-black text-[11px] font-bold uppercase mr-2" data-testid={`invoice-save-${r.invoice_number}`}>
                    <Download className="h-3 w-3" /> Save
                  </button>
                  <button onClick={() => send(r.invoice_number)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-black text-[11px] font-bold uppercase" data-testid={`invoice-send-${r.invoice_number}`}>
                    <Send className="h-3 w-3" /> Send
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
